import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createInitialRuntimeState } from './data/gameState';
import { getGameLocation } from './data/gameLocations';
import { getChallengeScene } from './data/shared/challengeSettings';
import {
  GAME_CONFIG,
  getHitsUntilLose,
  getStageForElapsedTime,
  getPenaltyForTime,
} from './data/gameConfig';
import {
  buildSessionTimelines,
  getCameraSampleForTime,
} from './data/timelines';
import { AudioEngine } from './infrastructure/AudioEngine';
import { getAccuracy, getHitWindow, getHoldOutcome, getHoldProgress, judgeHit, JUDGEMENTS, registerHit } from './data/rhythm';
import './presentation/rhythm.css';
import { ensureThreeJS } from './infrastructure/ensureThree';
import { preloadImageAssets } from './infrastructure/gameAssetLoader';
import { preloadCelebrationAudio, playCelebrationAudio } from './infrastructure/celebrationAudio';
import { updateLifeHud, updateHoldHud } from './infrastructure/gameHudFeedback';
import { fetchGameMenuConfig, getDefaultGameMenuConfig } from './infrastructure/gameMenuConfigApi';
import { startPlayAttempt } from './infrastructure/rankingApi';
import { loadActiveTournament, sceneLocationIdForZone } from './infrastructure/tournamentLinks';
import { VerifiedPlaySession } from './infrastructure/VerifiedPlaySession';
import {
  getGameImageAssets,
  createRoadSurface,
  createRoadDecals,
  setSpriteSheetFrame,
  advanceSpriteAnimation,
  createRunnerCharacter,
  updateRunnerExpression,
  cycleRunnerCelebration,
  setRunnerTempo,
  createRunnerCurse,
  attachCurseToEnvironment,
  updateRunnerCurse,
  resetRunnerCurse,
  createAtmosphere,
  createAmbientAnimals,
  createParkWaterLife,
  createParkAirLife,
  disposeResources,
  createSquareHitGeometry,
  createHistoricCorridor,
} from './infrastructure/gameScene';
import CountdownOverlay from './presentation/components/CountdownOverlay';
import GameLoadingOverlay from './presentation/components/GameLoadingOverlay';
import GameHud from './presentation/components/GameHud';
import MenuOverlay from './presentation/components/MenuOverlay';
import NohoCredit from './presentation/components/NohoCredit';
import StreakOverlay from './presentation/components/StreakOverlay';
import TelemetryDashboard from './presentation/components/TelemetryDashboard';
import TouchControls from './presentation/components/TouchControls';

const DEFAULT_GAME_MENU_CONFIG = getDefaultGameMenuConfig();
const HIT_REWARD_POOL_SIZE = 8;

const yieldToMainThread = () => (
  window.scheduler?.yield ? window.scheduler.yield() : new Promise((resolve) => window.setTimeout(resolve, 0))
);

const withTimeout = (promise, timeoutMs, message) => {
  let timeoutId;
  const timeout = new Promise((_, reject) => {
    timeoutId = window.setTimeout(() => reject(new Error(message)), timeoutMs);
  });
  return Promise.race([promise, timeout]).finally(() => window.clearTimeout(timeoutId));
};

export default function GameShell({ onComplete, locationCoords = null }) {
  const mountRef = useRef(null);
  const dangerVignetteRef = useRef(null);
  const scoreTextRef = useRef(null);
  const streakTextRef = useRef(null);
  const multiplierTextRef = useRef(null);
  const rhythmHudRef = useRef({});
  const feedbackRefs = useRef({});
  const holdFeedbackRefs = useRef({});
  const inputSourcesRef = useRef({ left: new Set(), right: new Set() });
  const pausePromiseRef = useRef(null);
  const evidenceRef = useRef(null);
  const renderedHudRef = useRef({});
  const pauseHandlerRef = useRef(null);
  const [saveStatus, setSaveStatus] = useState('');
  const playAttemptRef = useRef(null);

  const gameEngine = useRef({});
  const audioRef = useRef(new AudioEngine());

  const [gameState, setGameState] = useState('menu');
  const [countdownNum, setCountdownNum] = useState(null);
  const [streakAnnouncer, setStreakAnnouncer] = useState(null);
  const [finalStats, setFinalStats] = useState(null);
  const [menuConfig, setMenuConfig] = useState(DEFAULT_GAME_MENU_CONFIG);
  const [isMenuConfigLoaded, setIsMenuConfigLoaded] = useState(false);
  const [toleranceLevel, setToleranceLevel] = useState(DEFAULT_GAME_MENU_CONFIG.controls.tolerance.defaultValue);
  const [speedMultiplier, setSpeedMultiplier] = useState(DEFAULT_GAME_MENU_CONFIG.controls.speed.defaultValue);
  const [isEngineReady, setIsEngineReady] = useState(false);
  const [engineLoading, setEngineLoading] = useState({
    progress: 2,
    message: 'Cargando el motor 3D…',
    error: '',
  });
  const [startupError, setStartupError] = useState('');
  const [isStarting, setIsStarting] = useState(false);
  const [isResuming, setIsResuming] = useState(false);
  const [pauseError, setPauseError] = useState('');
  const [playMode, setPlayMode] = useState(() => new URLSearchParams(window.location.search).get('modo') || 'solo');
  const [shouldAutoStart] = useState(() => new URLSearchParams(window.location.search).get('autostart') === '1');
  // Torneo global: configuración fija del servidor; los ajustes locales se
  // ocultan para que nadie crea que puede cambiar la partida común.
  const [activeTournamentEntry] = useState(() => (
    new URLSearchParams(window.location.search).get('modo') === 'tournament' ? loadActiveTournament() : null
  ));
  const isTournamentMode = playMode === 'tournament';
  const tournamentSettings = activeTournamentEntry?.tournament?.settings || null;
  const playerName = localStorage.getItem('nacanaca-player') || 'Invitado';
  // En torneo la zona del admin es mandatoria: se lee del torneo unido, nunca
  // del GPS tardío que pueda haber reescrito el almacenamiento de sesión.
  const playerZone = isTournamentMode && tournamentSettings
    ? tournamentSettings.zone
    : (sessionStorage.getItem('nacanaca-zone') || 'Toluca');
  const [locationId] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('modo') === 'tournament') {
      const settings = loadActiveTournament()?.tournament?.settings;
      if (settings?.sceneLocationId) return getGameLocation(settings.sceneLocationId).id;
      if (settings?.zone) return getGameLocation(sceneLocationIdForZone(settings.zone)).id;
    }
    if (params.get('modo') === 'vs') {
      try {
        const active = JSON.parse(sessionStorage.getItem('nacanaca-active-challenge') || 'null');
        const id = active?.id || active?.challenge?.id;
        if (id) return getChallengeScene(id).id;
      } catch { /* Fall back to the selected scene when no invitation is cached. */ }
    }
    return getGameLocation(sessionStorage.getItem('nacanaca-scene-location')).id;
  });

  const stateRef = useRef(createInitialRuntimeState());

  useEffect(() => {
    const controller = new AbortController();
    fetchGameMenuConfig(controller.signal)
      .then(({ config }) => {
        if (controller.signal.aborted) return;
        setMenuConfig(config);
        setToleranceLevel(config.controls.tolerance.defaultValue);
        setSpeedMultiplier(config.controls.speed.defaultValue);
        setIsMenuConfigLoaded(true);
      })
      .catch((error) => {
        if (error.name !== 'AbortError') {
          console.warn('Se usara la configuracion local del menu.', error);
          setIsMenuConfigLoaded(true);
        }
      });
    return () => controller.abort();
  }, []);

  // Evita el parpadeo de los controles de ajuste: mientras la configuracion
  // remota no llega, se ocultan aunque el default local los muestre.
  // En torneos siempre se ocultan: la configuración la fija el servidor.
  const effectiveMenuConfig = isTournamentMode
    ? {
      ...menuConfig,
      presentation: {
        ...menuConfig.presentation,
        description: tournamentSettings
          ? `Torneo “${activeTournamentEntry.tournament.name}” · velocidad ${Number(tournamentSettings.speedMultiplier).toFixed(1)} · ventana ${Number(tournamentSettings.toleranceLevel).toFixed(1)} · ${tournamentSettings.zone}. Todas las personas participantes juegan igual.`
          : 'Torneo global · configuración fija para todas las personas participantes.',
      },
      controls: { ...menuConfig.controls, showTuning: false },
    }
    : isMenuConfigLoaded
      ? menuConfig
      : { ...menuConfig, controls: { ...menuConfig.controls, showTuning: false } };

  const clearHitRewardEffects = useCallback((engine) => {
    if (!engine?.activeHitRewardEffects) return;
    engine.activeHitRewardEffects.forEach((effect) => {
      effect.mesh.visible = false;
      effect.active = false;
    });
    engine.activeHitRewardEffects = [];
  }, []);

  useEffect(() => {
    stateRef.current.currentTolerance = toleranceLevel;
  }, [toleranceLevel]);

  useEffect(() => {
    stateRef.current.currentSpeed = speedMultiplier;
    audioRef.current.setSpeed(speedMultiplier);
  }, [speedMultiplier]);

  const removeNoteMesh = useCallback((engine, note) => {
    if (!engine || !note?.mesh) return;
    engine.scene.remove(note.mesh);
    if (note.trailMesh) {
      engine.scene.remove(note.trailMesh);
    }
  }, []);

  const cleanupThreeJS = useCallback(() => {
    const engine = gameEngine.current;
    engine.disposed = true;
    engine.isRunning = false;
    if (engine.animationId) cancelAnimationFrame(engine.animationId);
    clearHitRewardEffects(engine);
    engine.activeNotes?.forEach((note) => removeNoteMesh(engine, note));
    if (engine.handleResize) window.removeEventListener('resize', engine.handleResize);
    if (engine.environmentGroup) {
      engine.scene?.remove(engine.environmentGroup);
    }
    if (engine.runnerCharacter?.mesh) {
      engine.scene?.remove(engine.runnerCharacter.mesh);
    }
    if (engine.runnerCurse) engine.scene?.remove(engine.runnerCurse.group);
    engine.hitRewardPool?.forEach((effect) => engine.scene?.remove(effect.mesh));
    if (engine.atmosphere?.group) engine.scene?.remove(engine.atmosphere.group);
    if (engine.ambientAnimals?.group) engine.scene?.remove(engine.ambientAnimals.group);
    if (engine.parkWaterLife?.group) engine.scene?.remove(engine.parkWaterLife.group);
    if (engine.parkAirLife?.group) engine.scene?.remove(engine.parkAirLife.group);
    disposeResources(engine.environmentResources);
    disposeResources([
      engine.runnerCharacter?.geometry,
      engine.runnerCharacter?.material,
      engine.runnerCharacter?.normalTexture,
      engine.runnerCharacter?.smileTexture,
      engine.runnerCharacter?.victoriaTexture,
      engine.runnerCharacter?.saltoTexture,
      ...(engine.runnerCurse?.resources || []),
      engine.hitRewardGeometry,
      engine.noteGeometry,
      engine.holdGeometry,
      ...(engine.atmosphere?.resources || []),
      ...(engine.ambientAnimals?.resources || []),
      ...(engine.parkWaterLife?.resources || []),
      ...(engine.parkAirLife?.resources || []),
      ...(engine.sceneResources || []),
    ]);
    if (engine.roadDecals) {
      engine.scene?.remove(engine.roadDecals.group);
      disposeResources(engine.roadDecals.resources);
    }
    if (engine.roadSurface) {
      engine.scene?.remove(engine.roadSurface);
      engine.roadSurface.geometry.dispose();
      engine.roadSurface.material.dispose();
    }
    engine.roadTexture?.dispose();
    if (engine.renderer && mountRef.current?.contains(engine.renderer.domElement)) {
      mountRef.current.removeChild(engine.renderer.domElement);
      engine.renderer.dispose();
    }
    if (dangerVignetteRef.current) {
      dangerVignetteRef.current.style.opacity = '0';
      dangerVignetteRef.current.style.background = 'radial-gradient(circle at center, rgba(4,6,10,0) 30%, rgba(4,6,10,0.22) 62%, rgba(0,0,0,0.7) 100%)';
    }
    audioRef.current.dispose();
  }, [clearHitRewardEffects, removeNoteMesh]);

  const initThreeJS = useCallback((loadedImages) => {
    const THREE = window.THREE;
    if (!THREE || !mountRef.current) throw new Error('El contenedor 3D no está disponible.');

    const width = mountRef.current.clientWidth;
    const height = mountRef.current.clientHeight;

    const scene = new THREE.Scene();
    const campus = locationId === 'ceu';
    const tollocan = locationId === 'tollocan';
    const backgroundColor = tollocan ? 0xadb9ae
      : campus ? 0x070f16 : locationId === 'parque' ? 0x102b30 : GAME_CONFIG.colors.bg;
    scene.background = new THREE.Color(backgroundColor);
    scene.fog = new THREE.Fog(backgroundColor, tollocan ? 24 : 10, tollocan ? 94 : campus ? 68 : 80);

    const camera = new THREE.PerspectiveCamera(70, width / height, 0.1, 100);
    const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: false });
    renderer.setSize(width, height);
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    mountRef.current.appendChild(renderer.domElement);

    const ambientLight = new THREE.AmbientLight(campus ? 0xb0d1df : 0xffffff, 0.6);
    scene.add(ambientLight);
    const dirLight = new THREE.DirectionalLight(campus ? 0xc3dfdb : 0xffffff, 0.8);
    dirLight.position.set(0, 20, 10);
    scene.add(dirLight);

    const { roadSurface, roadTexture } = createRoadSurface(THREE, renderer, loadedImages, locationId);
    scene.add(roadSurface);

    const roadDecals = createRoadDecals(THREE, renderer, loadedImages, roadSurface.position.y, locationId);
    scene.add(roadDecals.group);

    const atmosphere = createAtmosphere(THREE, renderer, loadedImages, locationId);
    scene.add(atmosphere.group);

    const {
      environmentGroup,
      environmentResources,
      environmentLoopGroups,
      environmentLoopLength,
      parkWater,
      streetDecor,
    } = createHistoricCorridor(THREE, renderer, loadedImages, roadSurface.position.y, locationId);
    scene.add(environmentGroup);

    camera.position.set(0, 5, 15);
    camera.lookAt(0, 0, GAME_CONFIG.hitZ - 5);

    const runnerCharacter = createRunnerCharacter(THREE, renderer, loadedImages, roadSurface.position.y);
    scene.add(runnerCharacter.mesh);

    const runnerCurse = createRunnerCurse(
      THREE, roadSurface.position.y, GAME_CONFIG.runner.staticZ,
      window.matchMedia('(prefers-reduced-motion: reduce)').matches,
    );
    scene.add(runnerCurse.group);
    attachCurseToEnvironment(runnerCurse, roadSurface, environmentGroup, roadDecals);

    const ambientAnimals = createAmbientAnimals(THREE, renderer, loadedImages, roadSurface.position.y, locationId);
    scene.add(ambientAnimals.group);
    const emptyParkLife = () => ({ group: new THREE.Group(), resources: [], items: [], random: Math.random });
    const parkWaterLife = locationId === 'parque'
      ? createParkWaterLife(THREE, renderer, loadedImages, roadSurface.position.y, environmentLoopLength)
      : emptyParkLife();
    const parkAirLife = locationId === 'parque'
      ? createParkAirLife(THREE, renderer, loadedImages, roadSurface.position.y, environmentLoopLength)
      : emptyParkLife();
    scene.add(parkWaterLife.group, parkAirLife.group);

    const receptorGeo = new THREE.RingGeometry(0.86, 1, 4, 1, Math.PI / 4);
    receptorGeo.scale(Math.SQRT2, Math.SQRT2, 1);
    const matLeft = new THREE.MeshPhongMaterial({ color: GAME_CONFIG.colors.left, emissive: GAME_CONFIG.colors.left, emissiveIntensity: 0.6, transparent: true, opacity: 0.8, side: THREE.DoubleSide });
    const matRight = new THREE.MeshPhongMaterial({
      color: GAME_CONFIG.colors.right,
      emissive: GAME_CONFIG.colors.right,
      emissiveIntensity: 0.6,
      transparent: true,
      opacity: 0.8,
      side: THREE.DoubleSide,
    });

    const receptorLeft = new THREE.Mesh(receptorGeo, matLeft);
    receptorLeft.position.set(GAME_CONFIG.laneX.left, 0, GAME_CONFIG.hitZ);
    receptorLeft.rotation.x = -Math.PI / 2;
    scene.add(receptorLeft);

    const receptorRight = new THREE.Mesh(receptorGeo, matRight);
    receptorRight.position.set(GAME_CONFIG.laneX.right, 0, GAME_CONFIG.hitZ);
    receptorRight.rotation.x = -Math.PI / 2;
    scene.add(receptorRight);

    const hitRewardGeometry = createSquareHitGeometry(THREE);
    const hitRewardPool = Array.from({ length: HIT_REWARD_POOL_SIZE }, (_, index) => {
      const lane = index % 2 === 0 ? 'left' : 'right';
      const material = new THREE.MeshBasicMaterial({
        color: GAME_CONFIG.colors[lane],
        transparent: true,
        opacity: 0.96,
        depthWrite: false,
        depthTest: false,
        side: THREE.DoubleSide,
        polygonOffset: true,
        polygonOffsetFactor: -2,
        polygonOffsetUnits: -2,
      });
      const mesh = new THREE.Mesh(hitRewardGeometry, material);
      mesh.position.set(GAME_CONFIG.laneX[lane], roadSurface.position.y + 0.035, GAME_CONFIG.hitZ);
      mesh.rotation.x = -Math.PI / 2;
      mesh.scale.setScalar(Math.SQRT2);
      mesh.renderOrder = 15;
      scene.add(mesh);
      return { lane, age: 0, duration: 0.48, mesh, active: false };
    });
    const noteMaterials = {};
    const holdMaterials = {};
    for (const lane of ['left', 'right']) {
      const color = GAME_CONFIG.colors[lane];
      noteMaterials[lane] = new THREE.MeshPhongMaterial({ color, emissive: color, emissiveIntensity: 0.5 });
      holdMaterials[lane] = new THREE.MeshPhongMaterial({
        color, emissive: color, emissiveIntensity: 0.3, transparent: true, opacity: 0.55,
      });
    }

    gameEngine.current = {
      THREE,
      scene,
      camera,
      renderer,
      roadSurface,
      roadTexture,
      roadDecals,
      atmosphere,
      environmentGroup,
      environmentResources,
      environmentLoopGroups,
      environmentLoopLength,
      parkWater,
      streetDecor,
      runnerCharacter,
      runnerCurse,
      ambientAnimals,
      parkWaterLife,
      parkAirLife,
      receptors: { left: receptorLeft, right: receptorRight },
      noteGeometry: new THREE.BoxGeometry(1.8, 0.25, 1),
      holdGeometry: new THREE.BoxGeometry(1.4, 0.22, 1),
      noteMaterials,
      holdMaterials,
      sceneResources: [
        receptorGeo,
        matLeft,
        matRight,
        ...Object.values(noteMaterials),
        ...Object.values(holdMaterials),
        ...hitRewardPool.map((effect) => effect.mesh.material),
      ],
      hitRewardGeometry,
      hitRewardPool,
      activeNotes: [],
      activeHitRewardEffects: [],
      clock: new THREE.Clock(),
      animationId: null,
      trauma: 0,
      isRunning: false,
      sessionTimelines: null,
      reducedMotion: window.matchMedia('(prefers-reduced-motion: reduce)').matches,
      inputFlashUntil: { left: 0, right: 0 },
      cameraSample: {},
    };

    // Force shader compilation and texture upload while the loader is visible.
    // Notes and pooled hit effects then have no first-use shader work in play.
    const warmupMeshes = ['left', 'right'].flatMap((lane) => [
      new THREE.Mesh(gameEngine.current.noteGeometry, noteMaterials[lane]),
      new THREE.Mesh(gameEngine.current.holdGeometry, holdMaterials[lane]),
    ]);
    warmupMeshes.forEach((mesh) => scene.add(mesh));
    // Upload even optional curse textures now, before quality can enable them in play.
    runnerCurse.trail.visible = true;
    runnerCurse.beam.visible = true;
    runnerCurse.beam.material.opacity = 0;
    renderer.compile(scene, camera);
    renderer.initTexture(runnerCharacter.smileTexture);
    renderer.initTexture(runnerCharacter.victoriaTexture);
    renderer.initTexture(runnerCharacter.saltoTexture);
    warmupMeshes.forEach((mesh) => scene.remove(mesh));
    hitRewardPool.forEach((effect) => { effect.mesh.visible = false; });
    renderer.render(scene, camera);
    runnerCurse.trail.visible = false;
    runnerCurse.beam.visible = runnerCurse.quality > 1;
    runnerCurse.beam.material.opacity = 0.55;

    const handleResize = () => {
      const resizeWidth = mountRef.current.clientWidth;
      const resizeHeight = mountRef.current.clientHeight;
      renderer.setSize(resizeWidth, resizeHeight);
      camera.aspect = resizeWidth / resizeHeight;
      camera.updateProjectionMatrix();
      renderer.render(scene, camera);
    };

    window.addEventListener('resize', handleResize);
    gameEngine.current.handleResize = handleResize;
    setIsEngineReady(true);
  }, [locationId]);

  useEffect(() => {
    let isMounted = true;
    const controller = new AbortController();

    const loadGame = async () => {
      try {
        setEngineLoading({ progress: 2, message: 'Cargando el motor 3D…', error: '' });
        await ensureThreeJS();
        if (!isMounted) return;
        setEngineLoading({ progress: 10, message: 'Cargando escenarios y personajes…', error: '' });
        const loadedImages = await preloadImageAssets(getGameImageAssets(locationId), {
          signal: controller.signal,
          onProgress: ({ loaded, total }) => {
            if (!isMounted || controller.signal.aborted) return;
            setEngineLoading({
              progress: 10 + (loaded / total) * 82,
              message: `Cargando recursos ${loaded} de ${total}…`,
              error: '',
            });
          },
        });
        if (!isMounted) return;
        setEngineLoading({ progress: 93, message: 'Preparando la canción…', error: '' });
        preloadCelebrationAudio();
        await audioRef.current.prepareSong();
        if (!isMounted) return;
        setEngineLoading({ progress: 95, message: 'Construyendo la ciudad…', error: '' });
        await yieldToMainThread();
        if (!isMounted) return;
        initThreeJS(loadedImages);
        setEngineLoading({ progress: 100, message: 'El juego está listo.', error: '' });
        setStartupError('');
      } catch (error) {
        if (error.name === 'AbortError') return;
        controller.abort();
        console.error('No se pudo preparar Three.js.', error);
        if (isMounted) {
          setEngineLoading({
            progress: 0,
            message: '',
            error: 'No se pudieron cargar todos los recursos. Revisa la conexión y vuelve a intentarlo.',
          });
        }
      }
    };

    loadGame();

    return () => {
      isMounted = false;
      controller.abort();
      evidenceRef.current?.dispose();
      cleanupThreeJS();
    };
  }, [cleanupThreeJS, initThreeJS, locationId]);

  const showFeedback = useCallback((lane, label, detail, color, kind = 'hit') => {
    stateRef.current.laneFeedback[lane] = {
      label, detail, color,
      until: audioRef.current.getSongTime() + GAME_CONFIG.rhythm.feedbackSeconds,
    };
    const element = feedbackRefs.current[lane];
    if (element) {
      element.firstElementChild.textContent = label;
      element.lastElementChild.textContent = detail;
      element.style.color = color;
      element.style.opacity = '1';
      element.dataset.kind = kind;
    }
  }, []);

  const showHoldFeedback = useCallback((lane, status, progress = 0) => {
    const time = audioRef.current.getSongTime();
    const feedback = {
      status, progress, until: status === 'holding' ? Infinity : time + 1.1,
    };
    stateRef.current.holdFeedback[lane] = feedback;
    updateHoldHud(holdFeedbackRefs.current[lane], feedback, time);
  }, []);

  const handleMiss = useCallback((lane, label = 'Se pasó', isHold = false) => {
    const runtimeState = stateRef.current;
    runtimeState.totalMisses += 1;

    const timeToLose = getPenaltyForTime(runtimeState.elapsedTime);
    runtimeState.timeLeft -= timeToLose;

    audioRef.current.playMiss();
    if (lane) audioRef.current.stopHold(lane);
    if (runtimeState.streakTime > 0) {
      if (gameEngine.current) gameEngine.current.trauma = 0.25;
    }

    runtimeState.streakTime = 0;
    runtimeState.streakStartedAt = null;
    runtimeState.combo = 0;
    runtimeState.multiplier = 1;
    runtimeState.smileUntil = 0;
    runtimeState.announcementUntil = 0;
    setStreakAnnouncer(null);
    audioRef.current.setStreak(0);
    if (lane) showFeedback(lane, label, 'Vuelve al pulso', '#fda4af', 'miss');
    if (lane && isHold) showHoldFeedback(lane, 'fail', runtimeState.holdFeedback[lane]?.progress || 0);
    updateLifeHud(rhythmHudRef.current, runtimeState.elapsedTime, runtimeState.timeLeft);
  }, [showFeedback, showHoldFeedback]);

  const rewardHit = useCallback((judgement) => {
    const runtime = stateRef.current;
    const previousMultiplier = runtime.multiplier;
    registerHit(runtime, judgement);
    if (runtime.multiplier > previousMultiplier || (runtime.combo >= 40 && runtime.combo % 20 === 0)) {
      setStreakAnnouncer({ combo: runtime.combo, multiplier: runtime.multiplier });
      runtime.announcementUntil = audioRef.current.getSongTime() + 1.15;
      runtime.smileUntil = runtime.announcementUntil;
      // Cada festejo alterna animación (sonrisa -> victoria -> salto) con su audio.
      if (gameEngine.current?.runnerCharacter) {
        cycleRunnerCelebration(gameEngine.current.runnerCharacter);
      }
      playCelebrationAudio();
    }
  }, []);

  const endGame = useCallback((isVictory) => {
    const engine = gameEngine.current;
    engine.isRunning = false;

    if (engine.animationId) {
      cancelAnimationFrame(engine.animationId);
      engine.animationId = null;
    }

    const finalSongTime = audioRef.current.getSongTime();
    audioRef.current.stopBGM();
    if (!isVictory) audioRef.current.playMiss();

    const runtimeState = stateRef.current;
    runtimeState.phase = 'ended';
    const completedStats = {
      score: Math.floor(runtimeState.score),
      time: Math.floor(runtimeState.elapsedTime),
      totalHits: runtimeState.totalHits,
      maxStreakTime: runtimeState.maxStreakTime,
      totalMisses: runtimeState.totalMisses,
      accuracy: getAccuracy(runtimeState),
      maxCombo: runtimeState.maxCombo,
      judgements: { ...runtimeState.judgements },
      isVictory,
    };
    setFinalStats(completedStats);
    const details = {
      attemptId: playAttemptRef.current?.id, mode: playMode, user: playerName,
      zone: playAttemptRef.current?.zone || playerZone,
    };
    setSaveStatus('Confirmando puntaje…');
    const completedSession = evidenceRef.current;
    completedSession?.finish(finalSongTime).then((result) => {
      const { play } = result;
      const official = {
        score: play.score, time: play.durationSec ?? play.duration_sec,
        totalHits: play.totalHits ?? play.total_hits,
        totalMisses: play.totalMisses ?? play.total_misses,
        maxCombo: play.maxCombo ?? play.max_combo,
        maxStreakTime: play.maxStreakSec ?? play.max_streak_sec,
        accuracy: play.accuracy, isVictory: Boolean(play.isVictory ?? play.is_victory),
        officialPoints: Number(result.officialPoints) || 0,
        awards: Array.isArray(result.awards) ? result.awards : [],
        challenge: result.challenge || null,
        tournament: result.tournament || null,
      };
      if (!engine.disposed && evidenceRef.current === completedSession) {
        setFinalStats(official);
        setGameState(official.isVictory ? 'victory' : 'gameover');
        setSaveStatus('Puntaje confirmado');
      }
      onComplete?.(official, details);
      window.dispatchEvent(new CustomEvent('nacanaca-achievements-sync'));
    }).catch((error) => { if (!engine.disposed && evidenceRef.current === completedSession) setSaveStatus(`Puntaje sin registrar: ${error.message}`); });
    playAttemptRef.current = null;
    setGameState(isVictory ? 'victory' : 'gameover');
  }, [onComplete, playMode, playerName, playerZone]);

  const spawnNote = useCallback((engine, spawnEvent) => {
    const { THREE, scene } = engine;
    const geo = engine.noteGeometry;
    const targetHitTime = spawnEvent.targetHitTime;
    const holdDuration = spawnEvent.holdDuration || 0;

    const createMesh = (laneType) => {
      const mesh = new THREE.Mesh(geo, engine.noteMaterials[laneType]);
      mesh.position.set(GAME_CONFIG.laneX[laneType], 0.15, GAME_CONFIG.spawnZ);
      scene.add(mesh);

      let trailMesh = null;
      if (holdDuration > 0) {
        const trailGeo = engine.holdGeometry;
        trailMesh = new THREE.Mesh(trailGeo, engine.holdMaterials[laneType]);
        trailMesh.position.set(GAME_CONFIG.laneX[laneType], 0.15, GAME_CONFIG.spawnZ - 1);
        scene.add(trailMesh);
      }

      engine.activeNotes.push({
        mesh,
        trailMesh,
        lane: laneType,
        targetHitTime,
        tick: spawnEvent.tick,
        holdDuration,
        headHit: false,
        pressed: false,
        holdConsumed: 0,
      });
    };

    spawnEvent.lanes.forEach(createMesh);
  }, []);

  const createHitRewardEffect = useCallback((engine, lane) => {
    const receptor = engine.receptors?.[lane];
    if (!receptor || !engine.hitRewardPool?.length) return;

    let effect = engine.hitRewardPool.find((candidate) => !candidate.active);
    if (!effect) {
      effect = engine.activeHitRewardEffects.shift();
    }
    const { mesh } = effect;
    effect.lane = lane;
    effect.age = 0;
    effect.active = true;
    mesh.material.color.setHex(GAME_CONFIG.colors[lane]);
    mesh.material.opacity = 0.96;
    mesh.position.set(
      receptor.position.x,
      engine.roadSurface.position.y + 0.035,
      receptor.position.z,
    );
    mesh.rotation.x = -Math.PI / 2;
    mesh.scale.setScalar(Math.SQRT2);
    mesh.renderOrder = 15;
    mesh.visible = true;

    receptor.material.emissive.setHex(GAME_CONFIG.colors.hitFeedback);
    receptor.material.emissiveIntensity = 2.4;
    receptor.material.opacity = 1;

    engine.activeHitRewardEffects.push(effect);
  }, []);

  const animate = useCallback(() => {
    const engine = gameEngine.current;
    if (!engine || !engine.scene || !engine.isRunning) return;

    const frameDelta = engine.clock.getDelta();
    const delta = Math.min(frameDelta, 0.05);
    const runtimeState = stateRef.current;
    const currentVisualTime = performance.now() / 1000;
    const gameTime = audioRef.current.getSongTime();
    // Lock world scroll to the song clock so road/decals never drift from notes.
    // Render delta keeps sprite animation smooth; song delta keeps travel in sync.
    const prevGameTime = Number.isFinite(runtimeState.prevGameTime) ? runtimeState.prevGameTime : gameTime;
    let songDelta = gameTime - prevGameTime;
    if (!Number.isFinite(songDelta) || songDelta < 0) songDelta = 0;
    songDelta = Math.min(songDelta, 0.05);
    runtimeState.prevGameTime = gameTime;
    const worldDelta = runtimeState.phase === 'playing' ? songDelta : delta;

    const currentSpeed = runtimeState.currentSpeed;
    const finalSpeed = GAME_CONFIG.noteSpeed * currentSpeed;

    if (runtimeState.phase === 'countdown') {
      if (gameTime >= 0) {
        runtimeState.phase = 'playing';
        setGameState('playing');
        setCountdownNum(null);
        runtimeState.gameStartTime = currentVisualTime;
      } else {
        const currentCount = Math.min(GAME_CONFIG.rhythm.countInBeats, Math.ceil(-gameTime / audioRef.current.countInterval));
        if (currentCount !== runtimeState.lastCountUI) {
          setCountdownNum(currentCount);
          runtimeState.lastCountUI = currentCount;
        }
      }
    }

    if (runtimeState.phase === 'playing') {
      runtimeState.elapsedTime = Math.max(0, gameTime);
      const activeStage = getStageForElapsedTime(runtimeState.elapsedTime);
      runtimeState.currentStageId = activeStage.id;
      // Only successful play starts a streak; waiting never earns a combo.
      runtimeState.streakTime = runtimeState.streakStartedAt === null
        ? 0 : runtimeState.elapsedTime - runtimeState.streakStartedAt;
      runtimeState.maxStreakTime = Math.max(runtimeState.maxStreakTime, runtimeState.streakTime);
      audioRef.current.setStreak(runtimeState.streakTime);
    }

    if (runtimeState.announcementUntil && gameTime >= runtimeState.announcementUntil) {
      runtimeState.announcementUntil = 0;
      setStreakAnnouncer(null);
    }
    for (const lane of ['left', 'right']) {
      const feedback = runtimeState.laneFeedback[lane];
      const element = feedbackRefs.current[lane];
      if (feedback && element) {
        const remaining = Math.max(0, feedback.until - gameTime);
        element.style.opacity = String(Math.min(1, remaining * 6));
        if (remaining === 0) runtimeState.laneFeedback[lane] = null;
      }
    }

    // Keep React off the hot path and update text at most twelve times/second.
    if (gameTime - runtimeState.lastHudUpdate >= 0.08) {
      runtimeState.lastHudUpdate = gameTime;
      const renderedHud = renderedHudRef.current;
      const score = Math.floor(runtimeState.score).toLocaleString();
      if (scoreTextRef.current && renderedHud.score !== score) scoreTextRef.current.textContent = score;
      if (streakTextRef.current && renderedHud.combo !== runtimeState.combo) streakTextRef.current.textContent = runtimeState.combo;
      const multiplier = `×${runtimeState.multiplier}`;
      if (multiplierTextRef.current && renderedHud.multiplier !== multiplier) multiplierTextRef.current.textContent = multiplier;
      const hud = rhythmHudRef.current;
      updateLifeHud(hud, runtimeState.elapsedTime, runtimeState.timeLeft);
      for (const lane of ['left', 'right']) {
        updateHoldHud(holdFeedbackRefs.current[lane], runtimeState.holdFeedback[lane], gameTime);
      }
      const comboVisible = String(runtimeState.combo > 1);
      const comboTier = String(runtimeState.multiplier);
      if (hud.combo) {
        if (renderedHud.comboVisible !== comboVisible) hud.combo.dataset.visible = comboVisible;
        if (renderedHud.comboTier !== comboTier) hud.combo.dataset.tier = comboTier;
      }
      const dangerHidden = getHitsUntilLose(runtimeState.elapsedTime, runtimeState.timeLeft) > 2;
      if (hud.danger && renderedHud.dangerHidden !== dangerHidden) hud.danger.hidden = dangerHidden;
      Object.assign(renderedHud, {
        score,
        combo: runtimeState.combo,
        multiplier,
        comboVisible,
        comboTier,
        dangerHidden,
      });
    }

    const elapsed = runtimeState.elapsedTime;
    const currentLeft = runtimeState.phase === 'playing' ? runtimeState.timeLeft : GAME_CONFIG.time.initial;

    if (dangerVignetteRef.current) {
      const danger = Math.max(0, 1 - getHitsUntilLose(elapsed, currentLeft) / 2.5);
      // Fixed artwork, compositor-only opacity: no full-screen gradient repaint.
      dangerVignetteRef.current.style.opacity = runtimeState.phase === 'playing' ? String(danger * 0.7) : '0';
    }

    const worldScrollStep = finalSpeed * GAME_CONFIG.ground.scrollFactor * worldDelta;
    updateRunnerCurse(engine.runnerCurse, worldScrollStep, frameDelta, currentVisualTime, gameTime);

    if (engine.runnerCharacter) {
      updateRunnerExpression(engine.runnerCharacter, runtimeState.smileUntil > 0 && gameTime < runtimeState.smileUntil);
      advanceSpriteAnimation(engine.runnerCharacter, delta);
      engine.runnerCharacter.mesh.position.set(
        engine.runnerCurse.runnerX,
        engine.roadSurface.position.y + (engine.runnerCharacter.height * 0.5),
        GAME_CONFIG.runner.staticZ,
      );
      engine.runnerCharacter.mesh.lookAt(
        engine.camera.position.x,
        engine.runnerCharacter.mesh.position.y,
        engine.camera.position.z,
      );
    }

    if (runtimeState.phase === 'playing') {
      if (currentLeft <= 0) {
        endGame(false);
        return;
      }
    }

    const distanceToTravel = Math.abs(GAME_CONFIG.spawnZ - GAME_CONFIG.hitZ);
    const timeToTravel = distanceToTravel / finalSpeed;

    const spawnTimeline = engine.sessionTimelines?.spawnTimeline ?? [];
    while (
      runtimeState.nextSpawnEventIndex < spawnTimeline.length
      && spawnTimeline[runtimeState.nextSpawnEventIndex].targetHitTime < gameTime + timeToTravel
    ) {
      spawnNote(engine, spawnTimeline[runtimeState.nextSpawnEventIndex]);
      runtimeState.nextSpawnEventIndex += 1;
    }

    if (engine.roadTexture) {
      const scrollUnit = GAME_CONFIG.ground.length / engine.roadTexture.repeat.y;
      const scrollStep = worldScrollStep / scrollUnit;
      engine.roadTexture.offset.y = (engine.roadTexture.offset.y + scrollStep) % 1;

      if (engine.environmentLoopGroups?.length && engine.environmentLoopLength) {
        engine.environmentLoopGroups.forEach((group) => {
          group.position.z += worldScrollStep;
          if (group.position.z >= engine.environmentLoopLength) {
            group.position.z -= engine.environmentLoopLength * 2;
          }
        });
      }

      if (engine.streetDecor) {
        advanceSpriteAnimation(engine.streetDecor.vendorAnimation, delta);
        const lampNodes = engine.streetDecor.lampFlickerNodes;
        for (let index = 0; index < lampNodes.length; index += 1) {
          const node = lampNodes[index];
          const phase = node.userData.lampFlickerPhase;
          const flicker = engine.reducedMotion
            ? 0.72
            : Math.max(0.22, 0.74
              + Math.sin(currentVisualTime * 7.3 + phase) * 0.12
              + Math.sin(currentVisualTime * 18.7 + phase * 1.9) * 0.08);
          if (node.isPointLight) node.intensity = flicker;
          if (node.userData.lampGlow) node.material.opacity = 0.3 + flicker * 0.55;
        }
      }

      if (engine.roadDecals) {
        const decalItems = engine.roadDecals.items;
        for (let index = 0; index < decalItems.length; index += 1) {
          const item = decalItems[index];
          item.mesh.position.z += worldScrollStep;
          if (item.mesh.position.z > GAME_CONFIG.ground.decals.recycleZ) {
            item.mesh.position.z -= engine.roadDecals.spawnSpan;
          }
          advanceSpriteAnimation(item, delta);
        }
      }

      if (engine.atmosphere?.cloudTexture) {
        engine.atmosphere.cloudTexture.offset.x = (engine.atmosphere.cloudTexture.offset.x + delta * 0.006) % 1;
      }

      if (engine.parkWater?.texture) {
        // El agua se desplaza con UV; la geometría y la orilla se mantienen
        // precalculadas para que el lago no añada trabajo por vértice.
        engine.parkWater.texture.offset.x = (engine.parkWater.texture.offset.x + delta * 0.012) % 1;
      }

      if (engine.ambientAnimals?.items) {
        engine.ambientAnimals.items.forEach((animal) => {
          // Los del parque se animan solo al huir; el resto siempre.
          if (!animal.park) advanceSpriteAnimation(animal, delta);
          if (animal.park) {
            // Conejo y ardilla: quietos junto al sendero hasta que el
            // diablito se acerca; entonces huyen rápido hacia el bosque.
            // Los sheets miran a la izquierda, así que huir a +x va espejado.
            const groundY = engine.roadSurface.position.y + animal.height * animal.groundFactor + 0.04;
            if (animal.state !== 'fleeing') {
              animal.state = 'idle';
              animal.mesh.position.z += worldScrollStep;
              animal.mesh.position.x = animal.baseX;
              animal.mesh.position.y = groundY;
              animal.mesh.rotation.set(0, 0, 0);
              animal.mesh.scale.set(1, 1, 1);
              if (animal.currentFrameIndex !== 0) {
                animal.currentFrameIndex = 0;
                animal.frameElapsed = 0;
                setSpriteSheetFrame(animal.texture, 0, animal.frameColumns, animal.frameRows);
              }
              const runnerZ = GAME_CONFIG.runner.staticZ;
              if (animal.mesh.position.z > runnerZ - 10 && animal.mesh.position.z < runnerZ + 8) {
                animal.state = 'fleeing';
                if (!(animal.fleeSpeed > 0)) animal.fleeSpeed = 6.5 + Math.random() * 3;
              } else if (animal.mesh.position.z > 24) {
                animal.mesh.position.z -= 110;
                animal.mesh.position.x = animal.baseX;
              }
              return;
            }
            advanceSpriteAnimation(animal, delta);
            animal.mesh.position.z += worldScrollStep;
            animal.mesh.position.x += (animal.fleeSpeed || 7) * delta;
            animal.mesh.position.y = groundY;
            animal.mesh.rotation.set(0, 0, 0);
            animal.mesh.scale.set(-1, 1, 1);
            if (animal.mesh.position.z > 26 || animal.mesh.position.x > 13.5) {
              animal.state = 'idle';
              animal.currentFrameIndex = 0;
              animal.frameElapsed = 0;
              setSpriteSheetFrame(animal.texture, 0, animal.frameColumns, animal.frameRows);
              animal.mesh.position.z -= 110;
              animal.mesh.position.x = animal.baseX;
            }
            return;
          }
          // travelDir -1 (de espaldas) recorre las vías en sentido contrario:
          // se aleja de la cámara en lugar de acercarse.
          animal.mesh.position.z += worldScrollStep * animal.speedFactor * (animal.travelDir ?? 1);
          if (engine.reducedMotion) {
            animal.mesh.position.x = animal.baseX;
            animal.mesh.position.y = engine.roadSurface.position.y + animal.height * (animal.groundFactor ?? 0.23);
            animal.mesh.lookAt(
              engine.camera.position.x,
              animal.mesh.position.y,
              engine.camera.position.z,
            );
          } else {
            // Trote natural: rebote vertical sincronizado con la zancada +
            // deriva lateral lenta alrededor de su carril, sin jitter.
            const stride = currentVisualTime * animal.bobFreq + animal.phase;
            const bounce = Math.abs(Math.sin(stride));
            animal.mesh.position.x = animal.baseX
              + Math.sin(currentVisualTime * animal.wanderFreq + animal.phase) * animal.wanderAmp;
            animal.mesh.position.y = engine.roadSurface.position.y
              + (animal.height * (animal.groundFactor ?? 0.23))
              + bounce * animal.bobAmp;
            animal.mesh.lookAt(
              engine.camera.position.x,
              animal.mesh.position.y,
              engine.camera.position.z,
            );
            // Squash & stretch sutil del galope + leve inclinacion.
            const stretch = Math.sin(stride) * 0.045;
            animal.mesh.scale.y = 1 + stretch;
            animal.mesh.scale.x = animal.side * (1 - stretch * 0.8);
            animal.mesh.rotation.z = Math.sin(stride * 0.5) * 0.05;
          }

          const travelDir = animal.travelDir ?? 1;
          const leftScene = travelDir > 0
            ? animal.mesh.position.z > GAME_CONFIG.ambientAnimals.recycleZ
            : animal.mesh.position.z < GAME_CONFIG.spawnZ;
          if (leftScene) {
            const conf = GAME_CONFIG.ambientAnimals || {};
            const lateralMin = conf.lateralMin ?? 5.6;
            const lateralMax = conf.lateralMax ?? 7.8;
            const speedMin = animal.speedMin ?? conf.speedMin ?? 0.55;
            const speedMax = animal.speedMax ?? conf.speedMax ?? 0.9;
            animal.side = engine.ambientAnimals.random() > 0.5 ? 1 : -1;
            animal.baseX = animal.side * (lateralMin + engine.ambientAnimals.random() * Math.max(0.5, lateralMax - lateralMin));
            animal.mesh.position.x = animal.baseX;
            animal.mesh.position.z = travelDir > 0
              // De frente: reaparece más allá del fondo y vuelve hacia la cámara.
              ? GAME_CONFIG.spawnZ - engine.ambientAnimals.random() * 34
              // De espaldas: reaparece tras la cámara y se aleja hacia el fondo.
              : GAME_CONFIG.ambientAnimals.recycleZ + 2 + engine.ambientAnimals.random() * 10;
            animal.mesh.scale.x = animal.side;
            animal.mesh.scale.y = 1;
            animal.mesh.rotation.z = 0;
            animal.speedFactor = speedMin + engine.ambientAnimals.random() * Math.max(0.1, speedMax - speedMin);
            animal.wanderFreq = 0.45 + engine.ambientAnimals.random() * 0.35;
            animal.wanderAmp = (conf.wanderAmpMin ?? 0.45)
              + engine.ambientAnimals.random() * Math.max(0.1, (conf.wanderAmpMax ?? 0.9) - (conf.wanderAmpMin ?? 0.45));
            animal.bobAmp = (conf.bobAmp ?? 0.21) * (0.85 + engine.ambientAnimals.random() * 0.3);
            animal.phase = engine.ambientAnimals.random() * Math.PI * 2;
          }
        });
      }

      if (engine.parkWaterLife?.items) {
        engine.parkWaterLife.items.forEach((animal) => {
          advanceSpriteAnimation(animal, delta);
          // Nado lento de ida y vuelta entre minX y maxX, mirando hacia
          // donde avanzan (los sheets miran a la izquierda), con leve flotación.
          animal.mesh.position.x += animal.horizontalDirection * animal.speed * delta;
          if (animal.mesh.position.x <= animal.minX) {
            animal.mesh.position.x = animal.minX;
            animal.horizontalDirection = 1;
          } else if (animal.mesh.position.x >= animal.maxX) {
            animal.mesh.position.x = animal.maxX;
            animal.horizontalDirection = -1;
          }
          animal.mesh.scale.set(animal.horizontalDirection > 0 ? -1 : 1, 1, 1);
          animal.mesh.position.z += worldScrollStep;
          if (animal.mesh.position.z > animal.recycleZ) {
            animal.mesh.position.z -= animal.loopLength;
            // Reaparece en otro punto del ancho para cubrir más lugares.
            animal.mesh.position.x = animal.minX
              + engine.parkWaterLife.random() * (animal.maxX - animal.minX);
            animal.segmentIndex = (animal.segmentIndex + 1) % 2;
          }
          animal.mesh.position.y = animal.baseY + Math.sin(currentVisualTime * 1.4 + animal.phase) * 0.07;
          animal.mesh.rotation.set(0, 0, 0);
        });
      }

      if (engine.parkAirLife?.items) {
        engine.parkAirLife.items.forEach((bird) => {
          bird.mesh.position.z += worldScrollStep;
          if (bird.mesh.position.z > bird.recycleZ) {
            bird.mesh.position.z -= bird.loopLength;
          }
          bird.mesh.position.y = bird.baseY + Math.sin(currentVisualTime * bird.bobFreq + bird.phase) * bird.bobAmp;
        });
      }
    }

    const holdBonusPerSecond = GAME_CONFIG.notes.hold.bonusPerSecond;

    for (let index = engine.activeNotes.length - 1; index >= 0; index -= 1) {
      const note = engine.activeNotes[index];
      const timeUntilHit = note.targetHitTime - gameTime;

      if (note.holdDuration > 0) {
        const isHolding = note.headHit && note.pressed;
        note.mesh.position.z = GAME_CONFIG.hitZ - Math.max(timeUntilHit, 0) * finalSpeed;

        // Holds end at their chart timestamp, even after a slow frame or
        // an early head hit. Never integrate their duration using frame delta.
        const consumed = note.headHit ? getHoldProgress(note, note.releasedAt ?? gameTime) : 0;
        if ((isHolding || note.releasedAt != null) && runtimeState.phase === 'playing') {
          const earned = Math.max(0, consumed - note.holdConsumed);
          runtimeState.score += holdBonusPerSecond * runtimeState.multiplier * earned;
        }
        note.holdConsumed = consumed;
        const holdFeedback = runtimeState.holdFeedback[note.lane];
        if (note.headHit && holdFeedback && note.releasedAt == null) {
          holdFeedback.progress = consumed / note.holdDuration;
          holdFeedback.status = isHolding ? 'holding' : 'resume';
        }
        const remaining = Math.max(0, note.holdDuration - consumed);

        if (note.trailMesh) {
          const trailLength = remaining * finalSpeed;
          if (trailLength > 0.05) {
            note.trailMesh.visible = true;
            note.trailMesh.scale.z = trailLength;
            note.trailMesh.position.z = note.mesh.position.z - trailLength * 0.5;
          } else {
            note.trailMesh.visible = false;
          }
        }

        const outcome = getHoldOutcome(note, gameTime, runtimeState.resumeHoldUntil);
        if (outcome === 'complete') {
          createHitRewardEffect(engine, note.lane);
          audioRef.current.stopHold(note.lane);
          audioRef.current.playHoldComplete();
          rewardHit(note.judgement);
          runtimeState.smileUntil = gameTime + 1.15;
          showFeedback(note.lane, '¡Completa!', 'Pulso sostenido', '#F7E04B', 'hold-success');
          showHoldFeedback(note.lane, 'success', 1);
          removeNoteMesh(engine, note);
          engine.activeNotes.splice(index, 1);
          continue;
        }

        const droppedEarly = outcome === 'miss';
        if (droppedEarly || (!note.headHit && timeUntilHit < -getHitWindow(runtimeState.currentTolerance))) {
          removeNoteMesh(engine, note);
          engine.activeNotes.splice(index, 1);
          if (runtimeState.phase === 'playing') handleMiss(note.lane, droppedEarly ? 'Soltaste antes' : 'Se pasó', true);
        }
        continue;
      }

      note.mesh.position.z = GAME_CONFIG.hitZ - timeUntilHit * finalSpeed;

      if (timeUntilHit < -getHitWindow(runtimeState.currentTolerance)) {
        removeNoteMesh(engine, note);
        engine.activeNotes.splice(index, 1);
        if (runtimeState.phase === 'playing') handleMiss(note.lane);
      }
    }

    if (runtimeState.phase === 'playing' && elapsed >= GAME_CONFIG.time.maxDuration) {
      endGame(runtimeState.timeLeft > 0);
      return;
    }

    const beatPhase = audioRef.current.getBeatPhase(gameTime);
    const scale = engine.reducedMotion ? 1 : 1 + (1 - beatPhase) ** 3 * 0.1;
    engine.receptors.left.scale.set(scale, scale, scale);
    engine.receptors.right.scale.set(scale, scale, scale);

    for (let index = engine.activeHitRewardEffects.length - 1; index >= 0; index -= 1) {
      const effect = engine.activeHitRewardEffects[index];
      effect.age += delta;
      const progress = Math.min(effect.age / effect.duration, 1);
      const squareScale = Math.SQRT2 + (1 - ((1 - progress) ** 3)) * 1.65;
      effect.mesh.scale.setScalar(squareScale);
      effect.mesh.material.opacity = Math.max(0, 0.96 * ((1 - progress) ** 1.4));

      if (progress >= 1) {
        effect.mesh.visible = false;
        effect.active = false;
        engine.activeHitRewardEffects.splice(index, 1);
      }
    }

    ['left', 'right'].forEach((lane) => {
      const receptor = engine.receptors?.[lane];
      const hasActiveReward = engine.activeHitRewardEffects.some((effect) => effect.lane === lane);
      const hasHeldNote = engine.activeNotes.some((note) => note.lane === lane && note.headHit && note.pressed);
      if (receptor?.material?.emissive && !hasActiveReward && gameTime >= engine.inputFlashUntil[lane]) {
        if (hasHeldNote) {
          receptor.material.emissive.setHex(GAME_CONFIG.colors.hitFeedback);
          receptor.material.emissiveIntensity = 1.4;
          receptor.material.opacity = 1;
        } else {
          receptor.material.emissive.setHex(lane === 'left' ? GAME_CONFIG.colors.left : GAME_CONFIG.colors.right);
          receptor.material.emissiveIntensity = 0.18 + ((scale - 1) * 2.5);
          receptor.material.opacity = 0.8;
        }
      }
    });

    // Camera tiers are unlocked by the best streak of the run. A miss resets
    // scoring, but never downgrades a camera motion that was already earned.
    const cameraSample = getCameraSampleForTime(
      engine.sessionTimelines?.cameraTimeline,
      runtimeState.currentStageId,
      runtimeState.maxStreakTime,
      elapsed,
      engine.cameraSample,
    );

    let targetX = engine.reducedMotion ? 0 : cameraSample.targetX * GAME_CONFIG.rhythm.cameraMotionScale;
    let targetY = engine.reducedMotion ? 3.8 : 3.8 + (cameraSample.targetY - 3.8) * GAME_CONFIG.rhythm.cameraMotionScale;
    let targetZ = cameraSample.targetZ;
    const targetFov = engine.camera.aspect < 1 ? 90 : cameraSample.targetFov;
    const targetRotZ = engine.reducedMotion ? 0 : cameraSample.targetRotZ * GAME_CONFIG.rhythm.cameraMotionScale;

    if (engine.trauma > 0 && !engine.reducedMotion) {
      const shakeIntensity = engine.trauma * engine.trauma;
      const maxShake = 0.35;
      targetX += (Math.random() - 0.5) * 2 * maxShake * shakeIntensity;
      targetY += (Math.random() - 0.5) * 2 * maxShake * shakeIntensity;
      targetZ += (Math.random() - 0.5) * 2 * maxShake * shakeIntensity;
      engine.trauma -= delta * 4.5;
      if (engine.trauma < 0) engine.trauma = 0;
    }

    const { THREE } = engine;
    const cameraBlend = 1 - Math.exp(-10 * delta);
    engine.camera.position.x = THREE.MathUtils.lerp(engine.camera.position.x, targetX, cameraBlend);
    engine.camera.position.y = THREE.MathUtils.lerp(engine.camera.position.y, targetY, cameraBlend);
    engine.camera.position.z = THREE.MathUtils.lerp(engine.camera.position.z, targetZ, cameraBlend);
    engine.camera.lookAt(0, engine.camera.aspect < 1 ? -1 : 0, GAME_CONFIG.hitZ - 5);
    engine.camera.rotation.z += targetRotZ;
    engine.camera.fov = THREE.MathUtils.lerp(engine.camera.fov, targetFov, cameraBlend);
    engine.camera.updateProjectionMatrix();

    engine.renderer.render(engine.scene, engine.camera);
    engine.animationId = requestAnimationFrame(animate);
  }, [createHitRewardEffect, endGame, handleMiss, removeNoteMesh, rewardHit, showFeedback, showHoldFeedback, spawnNote]);

  const initCountdown = useCallback(async (selectedMode = playMode) => {
    const engine = gameEngine.current;
    if (!engine?.scene || engine.isStarting) return;
    engine.isStarting = true;
    setIsStarting(true);
    setStartupError('');
    setSaveStatus('');
    evidenceRef.current?.dispose();
    try {
      // Unlock audio directly from the user's gesture, before preparation.
      await withTimeout(
        audioRef.current.init(),
        8000,
        'El navegador no pudo habilitar el audio. Toca de nuevo para reintentar.',
      );
      if (engine.disposed) return;
      await audioRef.current.prepareSong();
      if (engine.disposed) return;
      const selectedPlayMode = typeof selectedMode === 'string' ? selectedMode : playMode;
      const selectedIsTournament = selectedPlayMode === 'tournament';
      let activeChallenge = null;
      try { activeChallenge = JSON.parse(sessionStorage.getItem('nacanaca-active-challenge') || 'null'); } catch { /* invalid cache */ }
      const storedTournament = selectedIsTournament ? loadActiveTournament() : null;
      const attemptResponse = await startPlayAttempt({
        mode: selectedPlayMode === 'vs' ? 'vs' : 'solo',
        zone: playerZone,
        speedMultiplier,
        toleranceLevel,
        challengeToken: selectedPlayMode === 'vs' ? activeChallenge?.token : undefined,
        challengeId: selectedPlayMode === 'vs' ? activeChallenge?.id : undefined,
        tournamentToken: selectedIsTournament ? storedTournament?.token : undefined,
        tournamentId: selectedIsTournament ? storedTournament?.id : undefined,
        // Sin GPS en torneos: la partida solo alimenta el ranking del torneo.
        location: selectedIsTournament ? undefined : locationCoords || undefined,
      });
      if (!attemptResponse?.attempt?.id) {
        throw new Error('El servidor no pudo abrir una partida verificada.');
      }
      if (engine.disposed) return;
      playAttemptRef.current = attemptResponse.attempt;
      evidenceRef.current = new VerifiedPlaySession(attemptResponse.attempt,
        () => audioRef.current.getSongTime(), (error) => {
          if (!engine.disposed) {
            pauseHandlerRef.current?.();
            setPauseError(`No se puede validar esta partida: ${error.message}`);
          }
        });
      if (engine.animationId) cancelAnimationFrame(engine.animationId);
      clearHitRewardEffects(engine);
      engine.activeNotes.forEach((note) => removeNoteMesh(engine, note));
      engine.activeNotes = [];
      engine.sessionTimelines = buildSessionTimelines({ speedMultiplier: attemptResponse.attempt.speedMultiplier, seed: attemptResponse.attempt.seed });
      audioRef.current.setSpeed(attemptResponse.attempt.speedMultiplier);
      setRunnerTempo(engine.runnerCharacter, attemptResponse.attempt.speedMultiplier);
      audioRef.current.setTimeline(engine.sessionTimelines.audioStageTimelines);
      audioRef.current.setNoteTimeline(engine.sessionTimelines.spawnTimeline);
      audioRef.current.setStreak(0);
      const runtimeState = stateRef.current;
      Object.assign(runtimeState, {
        ...createInitialRuntimeState(),
        phase: 'preparing',
        currentTolerance: attemptResponse.attempt.toleranceLevel,
        currentSpeed: attemptResponse.attempt.speedMultiplier,
        prevGameTime: NaN,
      });
      renderedHudRef.current = {};
      inputSourcesRef.current.left.clear();
      inputSourcesRef.current.right.clear();
      setPlayMode(selectedPlayMode);
      setCountdownNum(GAME_CONFIG.rhythm.countInBeats);
      setStreakAnnouncer(null);
      setFinalStats(null);
      engine.trauma = 0;
      engine.isRunning = false;
      engine.inputFlashUntil = { left: -Infinity, right: -Infinity };
      resetRunnerCurse(engine.runnerCurse);
      if (engine.runnerCharacter) {
        updateRunnerExpression(engine.runnerCharacter, false);
        engine.runnerCharacter.celebrationIndex = 0;
        engine.runnerCharacter.currentFrameIndex = 0;
        engine.runnerCharacter.frameElapsed = 0;
        setSpriteSheetFrame(engine.runnerCharacter.texture, 0, engine.runnerCharacter.frameColumns, engine.runnerCharacter.frameRows);
      }
      await evidenceRef.current.ready();
      if (engine.disposed) { evidenceRef.current.dispose(); return; }
      runtimeState.phase = 'countdown';
      setGameState('countdown');
      engine.isRunning = true;
      audioRef.current.startBGM();
      engine.clock.start();
      animate();
    } catch (error) {
      evidenceRef.current?.dispose();
      playAttemptRef.current = null;
      audioRef.current.stopBGM();
      engine.isRunning = false;
      if (!engine.disposed) {
        setGameState('menu');
        setCountdownNum(null);
        setStartupError(error.message || 'No se pudo iniciar la partida verificada. Inténtalo otra vez.');
      }
      console.error('No se pudo iniciar el ritmo.', error);
    } finally {
      engine.isStarting = false;
      if (!engine.disposed) setIsStarting(false);
    }
  }, [animate, clearHitRewardEffects, locationCoords, playMode, playerZone, removeNoteMesh, speedMultiplier, toleranceLevel]);

  const handleInput = useCallback((lane, source = 'input', inputTimestamp) => {
    const runtime = stateRef.current;
    if (!['playing', 'countdown'].includes(runtime.phase)) return;
    const engine = gameEngine.current;
    if (!engine?.activeNotes) return;
    const sources = inputSourcesRef.current[lane];
    if (sources.has(source)) return;
    const alreadyPressed = sources.size > 0;
    sources.add(source);
    if (alreadyPressed) return;

    const gameTime = audioRef.current.getSongTime(inputTimestamp);
    evidenceRef.current?.input(gameTime, lane, true);
    engine.receptors[lane].material.opacity = 1;
    engine.inputFlashUntil[lane] = gameTime + 0.1;
    const heldNote = engine.activeNotes.find((note) => note.lane === lane && note.headHit);
    if (heldNote) {
      if (getHoldOutcome(heldNote, gameTime, runtime.resumeHoldUntil) === 'miss') {
        handleMiss(lane, 'Soltaste antes', true);
        removeNoteMesh(engine, heldNote);
        engine.activeNotes.splice(engine.activeNotes.indexOf(heldNote), 1);
        return;
      }
      heldNote.pressed = true;
      heldNote.releasedAt = null;
      heldNote.holdConsumed = getHoldProgress(heldNote, gameTime);
      audioRef.current.startHold(lane, heldNote);
      showHoldFeedback(lane, 'holding', heldNote.holdConsumed / heldNote.holdDuration);
      return;
    }
    // Let the first note receive the same early window as every other note.
    if (runtime.phase === 'countdown' && gameTime < -getHitWindow(runtime.currentTolerance)) return;
    let closestNoteIdx = -1;
    let minTimeDiff = Infinity;
    engine.activeNotes.forEach((note, index) => {
      if (note.lane === lane && !note.headHit) {
        const distance = Math.abs(gameTime - note.targetHitTime);
        if (distance < minTimeDiff) { minTimeDiff = distance; closestNoteIdx = index; }
      }
    });
    const note = engine.activeNotes[closestNoteIdx];
    const offset = note ? gameTime - note.targetHitTime : 0;
    const judgement = note ? judgeHit(offset, runtime.currentTolerance) : null;
    if (judgement) {
      createHitRewardEffect(engine, lane);
      audioRef.current.playHit(lane, judgement, note);
      showFeedback(lane, JUDGEMENTS[judgement].label,
        note.holdDuration > 0 ? 'Mantén…' : judgement === 'perfect' ? 'En el pulso' : offset < 0 ? 'Un poco antes' : 'Un poco después',
        JUDGEMENTS[judgement].color);
      if (note.holdDuration > 0) {
        note.headHit = true;
        note.pressed = true;
        note.judgement = judgement;
        note.holdConsumed = getHoldProgress(note, gameTime);
        showHoldFeedback(lane, 'holding', note.holdConsumed / note.holdDuration);
      } else {
        rewardHit(judgement);
        removeNoteMesh(engine, note);
        engine.activeNotes.splice(closestNoteIdx, 1);
      }
    } else if (runtime.phase === 'playing') {
      handleMiss(lane, note && offset < 0 ? 'Muy pronto' : 'Fuera de pulso');
    }
  }, [createHitRewardEffect, handleMiss, removeNoteMesh, rewardHit, showFeedback, showHoldFeedback]);

  const handleRelease = useCallback((lane, source = 'input', inputTimestamp) => {
    if (!['playing', 'countdown'].includes(stateRef.current.phase)) return;
    const sources = inputSourcesRef.current[lane];
    sources.delete(source);
    if (sources.size > 0) return;
    evidenceRef.current?.input(audioRef.current.getSongTime(inputTimestamp), lane, false);
    const engine = gameEngine.current;
    if (!engine?.activeNotes) return;
    engine.activeNotes.forEach((note) => {
      if (note.lane === lane && note.pressed) {
        note.pressed = false;
        // Save the actual release time: a slow frame must not turn an early
        // release into a completed hold.
        note.releasedAt = audioRef.current.getSongTime(inputTimestamp);
        audioRef.current.stopHold(lane);
      }
    });
  }, []);

  const pauseGame = useCallback(() => {
    const runtime = stateRef.current;
    if (!['playing', 'countdown'].includes(runtime.phase)) return;
    const pauseTime = audioRef.current.getSongTime();
    runtime.resumePhase = runtime.phase;
    runtime.phase = 'paused';
    const engine = gameEngine.current;
    engine.isRunning = false;
    cancelAnimationFrame(engine.animationId);
    engine.animationId = null;
    inputSourcesRef.current.left.clear();
    inputSourcesRef.current.right.clear();
    engine.activeNotes.forEach((note) => {
      if (note.pressed) note.releasedAt = null;
      note.pressed = false;
    });
    pausePromiseRef.current = Promise.all([
      audioRef.current.pause(), evidenceRef.current?.pause(pauseTime),
    ]);
    pausePromiseRef.current.catch((error) => {
      if (!engine.disposed) setPauseError(error.message);
    });
    setCountdownNum(null);
    setGameState('paused');
  }, []);

  const resumeGame = useCallback(async () => {
    if (isResuming || gameEngine.current.isResuming) return;
    gameEngine.current.isResuming = true;
    setIsResuming(true);
    setPauseError('');
    try {
      // Call resume during the gesture, including on mobile Safari.
      // Unlock the audio device on the gesture, while the song clock stays paused.
      const unlocked = audioRef.current.prepareResume();
      await pausePromiseRef.current;
      await unlocked;
      await evidenceRef.current?.resume();
      await audioRef.current.resume();
      const engine = gameEngine.current;
      if (engine.disposed) return;
      const runtime = stateRef.current;
      runtime.phase = runtime.resumePhase;
      runtime.resumeHoldUntil = audioRef.current.getSongTime() + 0.6;
      runtime.lastHudUpdate = -Infinity;
      runtime.lastBeat = -Infinity;
      runtime.lastCountUI = -1;
      setGameState(runtime.phase);
      engine.clock.start();
      engine.isRunning = true;
      animate();
    } catch (error) {
      setPauseError(`No se pudo continuar: ${error.message}`);
    } finally {
      gameEngine.current.isResuming = false;
      setIsResuming(false);
    }
  }, [animate, isResuming]);

  useEffect(() => { pauseHandlerRef.current = pauseGame; }, [pauseGame]);

  useEffect(() => {
    const keyLane = { ArrowLeft: 'left', KeyA: 'left', ArrowRight: 'right', KeyL: 'right' };
    const handleKeyDown = (event) => {
      if (event.repeat || event.altKey || event.ctrlKey || event.metaKey) return;
      if (event.code === 'Escape') { pauseGame(); return; }
      if (keyLane[event.code] && ['playing', 'countdown'].includes(stateRef.current.phase)) {
        event.preventDefault();
        handleInput(keyLane[event.code], event.code, event.timeStamp);
      }
    };
    const handleKeyUp = (event) => {
      if (keyLane[event.code]) handleRelease(keyLane[event.code], event.code, event.timeStamp);
    };
    const handleVisibility = () => { if (document.hidden) pauseGame(); };
    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);
    window.addEventListener('blur', pauseGame);
    document.addEventListener('visibilitychange', handleVisibility);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
      window.removeEventListener('blur', pauseGame);
      document.removeEventListener('visibilitychange', handleVisibility);
    };
  }, [handleInput, handleRelease, pauseGame]);

  // iOS requires AudioContext.resume() to run in a fresh user gesture. The
  // portal navigation consumes the original tap, so direct entries wait for
  // one final tap after every visual resource has been verified.
  const waitsForDirectStart = shouldAutoStart
    && isEngineReady
    && gameState === 'menu'
    && !isStarting
    && !startupError;
  const showLoadingOverlay = !isEngineReady || isStarting || waitsForDirectStart;
  const loadingMessage = isStarting
    ? playMode === 'vs'
      ? 'Conectando el reto y sincronizando el pulso…'
      : playMode === 'tournament'
        ? 'Conectando el torneo y sincronizando el pulso…'
        : 'Sincronizando el pulso…'
    : engineLoading.message;

  return (
    <div className="rhythm-game relative w-full h-[100dvh] min-h-[100dvh] overflow-hidden bg-[#0A1A0F] font-sans select-none">
      <div ref={mountRef} className="absolute inset-0" />
      <div ref={dangerVignetteRef} className="rhythm-danger" />
      <TouchControls
        visible={gameState === 'playing' || gameState === 'countdown'}
        onInput={handleInput}
        onRelease={handleRelease}
        feedbackRefs={feedbackRefs}
        holdFeedbackRefs={holdFeedbackRefs}
      />
      <CountdownOverlay countdownNum={countdownNum} />
      <StreakOverlay streakAnnouncer={streakAnnouncer} />

      <GameHud
        gameState={gameState}
        scoreTextRef={scoreTextRef}
        streakTextRef={streakTextRef}
        multiplierTextRef={multiplierTextRef}
        rhythmHudRef={rhythmHudRef}
        onPause={pauseGame}
      />

      {(gameState === 'gameover' || gameState === 'victory') && (
        <div className="pointer-events-none absolute bottom-3 right-3 z-[60] flex justify-end">
          <NohoCredit variant="floating" />
        </div>
      )}

      <MenuOverlay
        visible={gameState === 'menu' && (!shouldAutoStart || Boolean(startupError))}
        config={effectiveMenuConfig}
        toleranceLevel={toleranceLevel}
        speedMultiplier={speedMultiplier}
        onToleranceChange={setToleranceLevel}
        onSpeedChange={setSpeedMultiplier}
        onStart={() => initCountdown()}
        isReady={isEngineReady && !isStarting}
        startupError={startupError}
      />

      <GameLoadingOverlay
        visible={showLoadingOverlay}
        progress={isStarting ? 100 : engineLoading.progress}
        message={loadingMessage}
        error={!isEngineReady ? engineLoading.error : ''}
        readyAction={waitsForDirectStart ? {
          label: playMode === 'vs' ? 'Entrar al reto' : playMode === 'tournament' ? 'Entrar al torneo' : 'Empezar a correr',
          onClick: () => initCountdown(),
        } : null}
        onRetry={() => window.location.reload()}
      />

      {saveStatus && <div className="rhythm-save-status" role="status">{saveStatus}</div>}
      {gameState === 'paused' && (
        <div className="rhythm-pause-overlay" role="dialog" aria-modal="true" aria-labelledby="pause-title">
          <h2 id="pause-title">Pulso en pausa</h2>
          <p>El tiempo de juego está detenido. Al continuar, retoma las notas largas que tengas abiertas.</p>
          {pauseError && <p role="alert">{pauseError}</p>}
          <button type="button" autoFocus onClick={resumeGame} disabled={isResuming}>{isResuming ? 'Sincronizando…' : 'Continuar'}</button>
          {pauseError && <button type="button" onClick={() => initCountdown()}>Iniciar otra partida</button>}
        </div>
      )}

      {(gameState === 'gameover' || gameState === 'victory') && (
        <div className="absolute inset-0 flex items-start justify-center overflow-y-auto py-6 pb-24 pointer-events-auto bg-black/80 backdrop-blur-sm z-50">
          <TelemetryDashboard
            stats={finalStats}
            onRestart={initCountdown}
            isVictory={gameState === 'victory'}
            isSaving={saveStatus === 'Confirmando puntaje…'}
            restartHint={isTournamentMode ? 'Tu puntaje del torneo ya quedó registrado. Solo se permite una partida por persona.' : ''}
          />
        </div>
      )}
    </div>
  );
}
